/**
 * The ONE AFFDO card's adapter: a submitter's (scan method, reference level)
 * choice → the flow the server should compile, plus the parameters it binds
 * (fs#1798).
 *
 * **Why an adapter, and why here.** AFFDO is one parameterization; the scan
 * method and the reference level are choices inside it
 * (`AFFDO_SCAN_METHODS_PLAN.md`, FROZEN rev 6). Today the server still serves
 * that one thing as two flows that differ only in their scan node
 * (`full_affdo_sequential`, `full_affdo_wavefront`). The operator's direction
 * (2026-09-29) was to ship the single card NOW, ahead of that plan's single
 * `full_affdo` flow, as an adapter over the two. Everything that knows the two
 * flows exist lives in this file, so when `full_affdo` lands the rebind is:
 * {@link METHOD_FLOWS} names it for every method, {@link resolveAffdoChoice}
 * adds `scan_method` to the parameters, and the card does not change. Until the
 * rebind, a server that serves `full_affdo` alone gets plain template cards
 * ({@link partitionFlows}), not a card with nothing to run.
 *
 * **Refusals are stated, not discovered.** A combination AFFDO or FlowStack
 * refuses today is disabled on the card with its reason. The one refused
 * reference level (`qdpi2`) would be refused at submit anyway, by
 * `affdo.dft_constrained_opt`'s `invalid_combinations` (afs#572; its rule in
 * the pinned bundle's manifest), so the card only says so before the request
 * is sent. `dft-sp` runs since afs#590 gave the tile a single-point branch and
 * deleted its afs#541 rule, and `aimnet2-sp` since afs#593 deleted afs#587's. The MACE
 * pair on a charged ligand is different: the charge reaches the tile through
 * its `ligand` input, not a parameter, so no submit-time rule can see it and
 * the tile body refuses it only at RUNTIME (exit 2), after the scan has been
 * paid for — which is exactly the "submitted to fail later" this card exists
 * to prevent. The body stays the enforcement; disabling the pair here is UX.
 *
 * Pure data and functions: no React, no fetching. The card and the submit page
 * read it; `affdo-flow-adapter.test.ts` pins every (method, level) pair.
 */

/** The scan methods of `AFFDO_SCAN_METHODS_PLAN.md` §3.1, in display order. */
export const SCAN_METHODS = ["directional", "bidirectional", "wavefront"] as const;
export type ScanMethod = (typeof SCAN_METHODS)[number];

/** AFFDO's seven reference levels, then FlowStack's two AIMNet2 levels (which
 * AFFDO does not have), in the order its flows declare them
 * (`reference_level.values` on both runnable flows). */
export const REFERENCE_LEVELS = [
  "xtb",
  "dft-sp",
  "dft",
  "qdpi2-sp",
  "qdpi2",
  "mace-sp",
  "mace",
  "aimnet2-sp",
  "aimnet2",
] as const;
export type ReferenceLevel = (typeof REFERENCE_LEVELS)[number];

/** The flow parameter the card owns. The generic parameter form must not
 * render a second control for it. */
export const CARD_OWNED_PARAMETERS = ["reference_level"] as const;

/** Every flow name of the AFFDO family. Only the ones the card can represent
 * fold into it (see {@link partitionFlows}): `full_affdo` is listed so the
 * page treats it as AFFDO, but until {@link METHOD_FLOWS} maps a method to it
 * it stays a plain template beside the card rather than vanishing into a card
 * that offers no way to run it (Rupert PR-1860 r2). */
export const AFFDO_FLOW_NAMES: readonly string[] = [
  "full_affdo_sequential",
  "full_affdo_wavefront",
  "full_affdo",
];

/** Which served flow runs each method today; `null` = no flow does. */
const METHOD_FLOWS: Record<ScanMethod, string | null> = {
  directional: "full_affdo_sequential",
  // The bidirectional xtb scan does not exist in FlowStack yet: it is built by
  // the scan-methods epic (plan §3.1, ATTMOS/project-manager#624).
  bidirectional: null,
  wavefront: "full_affdo_wavefront",
};

export const SCAN_METHOD_INFO: Record<ScanMethod, { label: string; description: string }> = {
  directional: {
    label: "Directional",
    description:
      "xtb, one leg from the seed angle through 360°. AFFDO's default scan (torsional_scan_engine xtb).",
  },
  bidirectional: {
    label: "Bidirectional",
    description:
      "xtb, a forward and a backward leg merged point by point, lower energy wins (AFFDO bi_directional_torsional_scan).",
  },
  // The key stays `wavefront` (the flow's name) until the scan-methods plan
  // rebinds the card; the text says what that flow runs: its
  // `ffpopt_scan_mode` defaults to AFFDO's seeded `direct` (afs#508), and a
  // charged fragment scans with the charge read from the parm7 (afs#511).
  wavefront: {
    label: "FFPOpt scan",
    description:
      "FFPOpt on the ffpopt-torch image, at xtb. Runs the direct scan by default (AFFDO's ffpopt_scan_mode direct); wavefront is selectable under the expert parameters. Charged fragments scan, with the charge read from the parm7 as AFFDO does.",
  },
};

/** What both AIMNet2 levels share (AIMNet2 plan C4, C8): the model's own
 * element set, the charge rule, and the singlet limit the level's text must
 * state, because the tile checks electron parity, not the spin state. */
const AIMNET2_SCOPE =
  "Charged ligands allowed. Elements: H, B, C, N, O, F, Si, P, S, Cl, As, Se, Br, I. Runs as a singlet, so the ligand must have an even electron count; a high-spin ligand is computed as a singlet.";

/** What `aimnet2-sp`'s like-for-like DFT//XTB gate measured (afs#585,
 * R-594-1, D110). It FAILED that gate on max (2.41 / 2.17 vs 2.0 kcal/mol;
 * RMSE ≤ 0.90 passes) and is offered by operator decision (2026-10-09, afs#593).
 * The max excess is attributed to the wB97M-D3 vs PBE0-D3BJ functional
 * difference, observed on one charged ligand and not isolated from AIMNet2's
 * own model error: at identical DFT-optimised geometries AIMNet2 vs DFT gave
 * max 2.41 on tor-0 (flowstack#2031's diagnosis), which rules out geometry; a
 * wB97M-D3 DFT control, affdo-flowstack#592, would test the functional
 * attribution: "confirm" would assume its result (fs#2049). The text states all
 * of that and never words it as a pass (fs#2045). Exported for the tests only. */
export const AIMNET2_SP_CAVEAT =
  "It failed the like-for-like check against DFT//XTB (PBE0-D3BJ, one charged ligand) on maximum error: 2.41 and 2.17 kcal/mol at the highest-energy scan points, against a 2.0 kcal/mol bound; its RMSE, at most 0.90 kcal/mol, met the 1.0 kcal/mol bound. Offered by operator decision (2026-10-09). The excess is attributed to the functional difference (wB97M-D3 vs PBE0-D3BJ), but that was observed on one charged ligand and is not separated from AIMNet2's own model error: on identical DFT-optimised geometries for one torsion, AIMNet2 still differed from DFT by up to 2.41 kcal/mol, which rules out the geometry. A wB97M-D3 DFT control (affdo-flowstack#592) would test the attribution, separating the functional difference from AIMNet2's model error.";

export const REFERENCE_LEVEL_INFO: Record<ReferenceLevel, string> = {
  xtb: "The xtb scan is the reference; no refinement runs.",
  "dft-sp": "DFT single points on the scan geometries.",
  dft: "Constrained DFT optimisation at every scan point.",
  "qdpi2-sp":
    "QDπ2 single points on the scan geometries, run through FFPOpt on one GPU as AFFDO runs them. Two checks on an RTX 5090, each within the float32 bound of 2.6e-3 eV (about 0.06 kcal/mol, 6× looser than MACE's 0.01 kcal/mol): its relative energies replayed against AFFDO's recorded qdpi2-sp run, and its GPU energies against the same image on the CPU. Both measured at most 2.3e-6 eV (afs#504).",
  qdpi2: "Constrained QDπ2 optimisation at every scan point.",
  "mace-sp":
    "MACE single points on the scan geometries, run through FFPOpt as AFFDO runs them. Replayed against AFFDO's recorded mace-sp run within 0.01 kcal/mol (max difference 0.000 eV, vcm#2211). Needs a neutral ligand.",
  mace: "Constrained MACE optimisation at every scan point, run through FFPOpt. Same FFPOpt command as AFFDO; no AFFDO numeric reference exists for this level. Needs a neutral ligand.",
  "aimnet2-sp": `AIMNet2 (wB97M-D3) single points on the xtb scan geometries, run through FFPOpt on the CPU. A FlowStack level; AFFDO has no AIMNet2 level. ${AIMNET2_SCOPE} ${AIMNET2_SP_CAVEAT}`,
  aimnet2: `Constrained AIMNet2 (wB97M-D3) optimisation at every scan point, run through FFPOpt on the CPU. A FlowStack level; AFFDO has no AIMNet2 level. ${AIMNET2_SCOPE}`,
};

/** Why each refused level cannot run today. Absent = runnable. `qdpi2`'s is
 * the `affdo.dft_constrained_opt` refusal as its manifest words it (afs#572):
 * afs#504's GPU check covers single points, so `qdpi2-sp` runs. `aimnet2-sp`
 * runs since afs#593 deleted afs#587's rule. */
const REFUSED_LEVELS: Partial<Record<ReferenceLevel, string>> = {
  qdpi2:
    "Refused for now: its constrained optimisation runs the qdpi2 model on the GPU, and the GPU numeric check that passed for it (afs#504) covers single-point energies and forces only. An optimisation needs a force-sized GPU bound, which is not defined yet. Use qdpi2-sp for a qdpi2 single-point reference, mace or mace-sp for an ML reference, dft for a DFT reference, or xtb.",
};

/** The flow input that carries the ligand on both AFFDO flows; its charge
 * decides whether the MACE pair can run. */
export const LIGAND_INPUT = "ligand";

/**
 * The selected ligand's net charge, as the catalog reports it
 * (`Artifact.molecule?.formal_charge`): a number, `null` when the stored
 * Molecule carries no readable charge, or `undefined` when there is no
 * `molecule` block (or no ligand chosen yet).
 */
export type LigandCharge = number | null | undefined;

/**
 * AFFDO's advice after a MACE refusal names its charged-ligand alternative.
 * The card never points at a level it shows disabled, so while that
 * alternative is refused here too, the advice says so and names the levels
 * that do run.
 */
function chargedLigandAdvice(alternative: ReferenceLevel): string {
  return REFUSED_LEVELS[alternative] === undefined
    ? `Please use ${alternative} instead.`
    : `AFFDO's alternative, ${alternative}, is disabled here too; use dft or xtb.`;
}

/**
 * Why a level cannot run on a ligand of this charge, or `undefined` when it
 * can. AFFDO refuses MACE on a charged ligand, and FlowStack refuses `mace-sp`
 * too rather than substitute `qdpi2-sp` (plan D5 = B). The refusal sentence
 * is AFFDO's (plan C10); the advice after it is {@link chargedLigandAdvice}'s.
 * Only a non-zero number refuses: an unknown charge (`null` or `undefined`)
 * is left to the tile body, which refuses an unreadable charge itself.
 */
function chargedLigandRefusal(level: ReferenceLevel, charge: LigandCharge): string | undefined {
  if (typeof charge !== "number" || charge === 0) return undefined;
  if (level === "mace") {
    return `MACE doesnt support charged molecules. ${chargedLigandAdvice("qdpi2")}`;
  }
  if (level === "mace-sp") {
    return `MACE-SP requires a neutral ligand (charge=0), but got charge=${charge}. ${chargedLigandAdvice("qdpi2-sp")}`;
  }
  return undefined;
}

export type Availability = { available: true } | { available: false; reason: string };

export function isAffdoFlow(name: string | null | undefined): boolean {
  return name !== null && name !== undefined && AFFDO_FLOW_NAMES.includes(name);
}

/** Split the flows to RENDER into the AFFDO family (rendered as one card) and
 * the rest (rendered as before, in their original order).
 *
 * `servedFlows` is the COMPLETE list `/api/flows` returned, kept apart from
 * `flows` so a filtered or searched subset can never flip the card-or-not
 * decision (Rupert PR-1860 r2): that is decided from what the server serves,
 * and `flows` only says which of them are on screen.
 *
 * **The card needs a method to run.** When no scan method maps to any served
 * AFFDO flow — the server serves only `full_affdo` before this adapter is
 * rebound to it — the card would be inert, so there is no card: every flow,
 * AFFDO ones included, passes through as a plain template, as before fs#1798,
 * and AFFDO never vanishes from the list.
 *
 * **The card holds only what it can represent**: a flow a scan method maps
 * to. An AFFDO flow it has no method for (`full_affdo`, served beside the old
 * flows before the rebind) stays a plain template next to it, reachable. */
export function partitionFlows<T extends { name: string }>(
  servedFlows: readonly string[],
  flows: readonly T[]
): { affdo: T[]; others: T[] } {
  const affdo: T[] = [];
  const others: T[] = [];
  for (const flow of flows) (isCardFlow(flow.name, servedFlows) ? affdo : others).push(flow);
  return { affdo, others };
}

/** Does `name` belong to the AFFDO card on a server serving `servedFlows`?
 * False for a non-AFFDO flow, for an AFFDO flow the card cannot represent,
 * and for every AFFDO flow when the card falls back to plain templates (see
 * {@link partitionFlows}). */
export function isCardFlow(
  name: string | null | undefined,
  servedFlows: readonly string[]
): boolean {
  return (
    isAffdoFlow(name) && methodForFlow(name) !== null && defaultScanMethod(servedFlows) !== null
  );
}

/** Can this method be submitted against the flows the server serves? */
export function scanMethodAvailability(
  method: ScanMethod,
  servedFlows: readonly string[]
): Availability {
  const flow = METHOD_FLOWS[method];
  if (flow === null) {
    return {
      available: false,
      reason:
        "Not available yet: FlowStack has no bidirectional xtb scan. It arrives with the single full_affdo flow (scan-methods epic, project-manager#624).",
    };
  }
  if (!servedFlows.includes(flow)) {
    return { available: false, reason: `This server does not serve ${flow}.` };
  }
  return { available: true };
}

/** Can this reference level be submitted? `offered` is the level enum the
 * resolved flow declares; a level missing from it would be refused by the
 * compiler, so it is refused here with that reason instead. `ligandCharge`
 * is the selected ligand's (see {@link LigandCharge}). */
export function referenceLevelAvailability(
  level: ReferenceLevel,
  offered?: readonly unknown[],
  ligandCharge?: LigandCharge
): Availability {
  const refused = REFUSED_LEVELS[level] ?? chargedLigandRefusal(level, ligandCharge);
  if (refused !== undefined) return { available: false, reason: refused };
  if (offered !== undefined && !offered.some((v) => String(v) === level)) {
    return { available: false, reason: "The selected flow does not offer this level." };
  }
  return { available: true };
}

/** The flow that runs this method today, or `null` when none does. Whether
 * the server actually serves it is {@link scanMethodAvailability}'s question. */
export function flowForMethod(method: ScanMethod): string | null {
  return METHOD_FLOWS[method];
}

/** The flow a served AFFDO flow name stands for, read back as a method.
 * `null` for an AFFDO flow no method maps to (`full_affdo` before the
 * rebind). */
export function methodForFlow(flowName: string | null | undefined): ScanMethod | null {
  for (const method of SCAN_METHODS) {
    if (METHOD_FLOWS[method] === flowName) return method;
  }
  return null;
}

/** The method the card opens on: AFFDO's default (directional) when it is
 * served, else the first one that is. `null` when none is. */
export function defaultScanMethod(servedFlows: readonly string[]): ScanMethod | null {
  return SCAN_METHODS.find((m) => scanMethodAvailability(m, servedFlows).available) ?? null;
}

export type AffdoResolution =
  | { ok: true; flowName: string; parameters: Record<string, unknown> }
  | { ok: false; reason: string };

/**
 * A (method, level) choice → the request the submit page sends.
 *
 * `level` is `undefined` when the flow's manifest declares no settable
 * `reference_level` (the page then submits none, as for any other hidden
 * parameter). The returned `parameters` are an OVERLAY on the page's own
 * parameter dict: today only `reference_level`; after the `full_affdo`
 * rebind, `scan_method` too. `ligandCharge` is the selected ligand's, so a
 * MACE level on a charged ligand is refused here as well as on the card.
 */
export function resolveAffdoChoice(
  choice: { method: ScanMethod | null; level: string | undefined },
  servedFlows: readonly string[],
  offeredLevels?: readonly unknown[],
  ligandCharge?: LigandCharge
): AffdoResolution {
  if (choice.method === null) return { ok: false, reason: "Pick a scan method." };
  const method = scanMethodAvailability(choice.method, servedFlows);
  if (!method.available) return { ok: false, reason: method.reason };
  const flowName = flowForMethod(choice.method) as string;
  if (choice.level === undefined) return { ok: true, flowName, parameters: {} };
  if (!(REFERENCE_LEVELS as readonly string[]).includes(choice.level)) {
    return { ok: false, reason: `Unknown reference level ${choice.level}.` };
  }
  const level = referenceLevelAvailability(
    choice.level as ReferenceLevel,
    offeredLevels,
    ligandCharge
  );
  if (!level.available) return { ok: false, reason: `${choice.level}: ${level.reason}` };
  return { ok: true, flowName, parameters: { reference_level: choice.level } };
}

/** The manifest the generic parameter form renders: the card's parameters
 * removed, so no knob appears twice. */
export function withoutCardOwnedParameters(
  manifest: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!manifest) return manifest;
  const params = manifest.parameters;
  if (!params || typeof params !== "object") return manifest;
  const rest = { ...(params as Record<string, unknown>) };
  for (const name of CARD_OWNED_PARAMETERS) delete rest[name];
  return { ...manifest, parameters: rest };
}
